import React, { useState, useCallback, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { apiService } from '@/services/api'

interface BRDUploaderProps {
  isOpen: boolean
  onClose: () => void
  onBRDProcessed: (rules: any[]) => void
}

interface BRDRule {
  id: number
  rule: string
  severity: 'critical' | 'warning' | 'minor'
  field: string
  section: string
  description: string
}

export default function BRDUploader({ isOpen, onClose, onBRDProcessed }: BRDUploaderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [rules, setRules] = useState<BRDRule[]>([])
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    const brdFile = files.find(file => 
      file.type === 'application/pdf' || 
      file.name.toLowerCase().includes('brd') ||
      file.type.includes('document')
    )
    
    if (brdFile) {
      processBRD(brdFile)
    }
  }, [])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processBRD(e.target.files[0])
    }
  }, [])

  const processBRD = async (file: File) => {
    setIsProcessing(true)
    setUploadedFile(file)

    try {
      const formData = new FormData()
      formData.append('brdFile', file)

      const result = await apiService.uploadFile('/api/brd/parse', formData)

      if (result.success) {
        setRules(result.data.rules || [])
        console.log('BRD processed successfully:', result.data.rules?.length, 'rules extracted')
      } else {
        throw new Error(result.message || 'Failed to process BRD')
      }
    } catch (error) {
      console.error('BRD processing error:', error)
      setRules([])
    } finally {
      setIsProcessing(false)
    }
  }

  const handleApplyRules = () => {
    onBRDProcessed(rules)
    onClose()
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
      case 'minor':
        return <CheckCircle2 className="w-4 h-4 text-blue-500" />
      default:
        return <CheckCircle2 className="w-4 h-4 text-gray-500" />
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Business Requirements Document (BRD)</DialogTitle>
        </DialogHeader>

        {!uploadedFile && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-700'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Upload BRD Document
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Upload your Business Requirements Document to extract Creative Matrix rules and compliance requirements.
            </p>
            <Button onClick={() => fileInputRef.current?.click()} className="gap-2">
              <Upload className="w-4 h-4" />
              Choose BRD File
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx"
            />
          </div>
        )}

        {isProcessing && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Processing BRD and extracting rules...</p>
          </div>
        )}

        {uploadedFile && !isProcessing && (
          <div className="space-y-6">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <strong>{uploadedFile.name}</strong> processed successfully. 
                {rules.length} compliance rules extracted.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Extracted BRD Rules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {rules.map((rule) => (
                    <div key={rule.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {getSeverityIcon(rule.severity)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {rule.section}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            rule.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            rule.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {rule.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">{rule.rule}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{rule.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleApplyRules}>
                Apply BRD Rules ({rules.length})
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
